import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BrandProfileForm,
  ErrorNotice,
  LoadingState,
  type ProfileSource,
} from '@bcecloud/portal-ui';
import { getJson, sendJson, uploadMedia, errorMessage } from '@/lib/api';

export interface MessagingProfileEditorProps {
  readonly organizationId: string;
  /** The org's brand, already loaded by the detail page. */
  readonly brand: object | null | undefined;
}

/**
 * BCE's side of a client's Brand and Messaging Profile.
 *
 * Renders the same `BrandProfileForm` the client's own admins use, so both
 * sides see identical fields and identical explanations — the two people most
 * likely to compare notes are an account director and their client.
 *
 * @param props - The organization and its brand record.
 * @returns The editor.
 */
export function MessagingProfileEditor({
  organizationId,
  brand,
}: MessagingProfileEditorProps): React.ReactElement {
  const queryClient = useQueryClient();
  const queryKey = ['messaging-profile', organizationId];

  const profile = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getJson<Record<string, unknown> | null>(
        `/organizations/${organizationId}/messaging-profile`,
        undefined,
        signal,
      ),
  });

  const save = useMutation({
    mutationFn: ({ source, patch }: { source: ProfileSource; patch: Record<string, unknown> }) =>
      sendJson(
        'put',
        source === 'brand'
          ? `/organizations/${organizationId}/brand`
          : `/organizations/${organizationId}/messaging-profile`,
        patch,
      ),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: variables.source === 'brand' ? ['organization', organizationId] : queryKey,
      });
    },
  });

  if (profile.isPending) return <LoadingState label="Loading messaging profile…" />;
  if (profile.isError) {
    return <ErrorNotice title="Could not load" message={errorMessage(profile.error)} />;
  }

  return (
    <BrandProfileForm
      brand={(brand as Record<string, unknown> | null) ?? null}
      messaging={profile.data}
      canEdit
      // Rethrown as a plain Error so the form can show it against the section
      // that failed rather than as a page-level banner.
      onSave={async (source, patch) => {
        try {
          await save.mutateAsync({ source, patch });
        } catch (err) {
          throw new Error(errorMessage(err));
        }
      }}
      upload={async (file) => (await uploadMedia(file, { organizationId, kind: 'logo' })).url}
    />
  );
}
